import React, { useState, useEffect, MouseEventHandler } from 'react';
import { useLocation } from 'react-router-dom';

import * as consts from 'config/consts';
import * as env from 'config/environment';
import * as api from 'config/api';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import {
  convertToMonoBrokerId,
  getAggregateSourceName,
  getDate,
} from 'components/features/utils/converter';
import {
  GetPositionsResultItem,
  GetUserBrokerResultItem,
  deleteAccount,
  deleteManualAsset,
  syncAccountAsset,
  refreshMoneytree,
} from 'config/api';
import { formatDateForString, formatNumber } from 'utils/formatter';

import { Group, isCategorized } from 'hooks/useGrouping';
import { useModal } from 'hooks/useModal';
import { useRouting } from 'hooks/useRouting';
import { AccountHistoryProps } from 'pages/AccountDetail';
import GoMoneytreeSiteDialog from 'components/features/MoveMoneytreeSiteDialog';
import PositionListAccountBreakdown from 'components/features/security-accounts/PositionList';
import Loading from 'components/commons/loading/Loading';
import Icon from 'components/commons/icon/Icon';
import { PositionListTransaction } from './PositionListTransaction';
import { DisplayableAssetTypeModalButton } from '../home/PortfolioStuffing/asset/AssetOrganizedPortfolioForeign';
import { DispType } from '../home/PortfolioStuffing/PortfolioStuffing';
import {
  AssetsChart,
  ChartDatum,
  DebtChart,
} from '../account-detail/UserBrokerItemChart';
import { getUserStorage } from '../utils/utils';
import SyncAccountAssetResultModal from '../modal/SyncAccountAssetResultModal';
import { NeedsMigrateCard } from '../modal/MigrateToMonoModal';
import { InformationFromMoneytreeModal } from '../modal/InformationFromMoneytreeModal';
import AccountDeleteConfirmModal from '../modal/AccountDeleteConfirmModal';
import { SecurityAccountCooperationErrorModal } from '../modal/SecurityAccountCooperationErrorModal';
import { AccountAlignmentModal } from '../modal/AccountAlignmentModal';
import { UpdateAlignmentAccountModal } from '../modal/UpdateAlignmentAccountModal';
import { AccountUpdateStoppedModal } from '../modal/AccountUpdateStoppedModal';
import { DuplicationAccountDeleteModal } from '../modal/DuplicationAccountDeleteModal';
import { WaitUntilNextUpdateModal } from '../modal/WaitUntilNextUpdateModalModal';
import { AlignmentAcountLoginInfoChangeModal } from '../modal/AlignmentAcountLoginInfoChangeModal';
import { AlignmentPreparationModal } from '../modal/AlignmentPreparationModal';
import { MonoSuspendedModal } from '../modal/MonoSuspendedModal';

type UserBrokerItemProps = {
  brokerItem: GetUserBrokerResultItem;
  brokerItems: GetUserBrokerResultItem[];
  onBrokerClick?: (item: GetUserBrokerResultItem) => void;
  onChangeState?: (item: GetUserBrokerResultItem) => void;
  onSyncAccountAsset?: (item: GetUserBrokerResultItem) => void;
  onRefreshMoneytree?: () => void;
  setUserPositions?: (positions: Array<GetPositionsResultItem>) => void;
  editable: boolean;
  positions?: Array<GetPositionsResultItem>;
  group?: Group | null;
  accountHistory?: AccountHistoryProps;
  getIsShowDisplayableAssetTypeModalButton: (arg: {
    brokerId: GetUserBrokerResultItem['BrokerID'];
  }) => boolean;
  isExistError900?: boolean;
  index?: number;
};

const POSITION_LIST_TYPE = {
  入出金: 'TRANSACTION',
  口座内訳: 'ACCOUNT_BREAKDOWN',
} as const;
type PositionListType = typeof POSITION_LIST_TYPE[keyof typeof POSITION_LIST_TYPE];

type UserBrokerItemInternalProps = UserBrokerItemProps & {
  positionListType: PositionListType;
};

const UserBrokerItemInternal: React.FC<UserBrokerItemInternalProps> = ({
  brokerItem,
  brokerItems,
  onBrokerClick,
  onChangeState,
  onSyncAccountAsset,
  onRefreshMoneytree,
  setUserPositions,
  editable,
  positions,
  group,
  accountHistory,
  positionListType,
  getIsShowDisplayableAssetTypeModalButton,
  isExistError900,
  index,
}) => {
  const [
    isShowInformationFromMoneytreeModal,
    {
      hideModal: hideInformationFromMoneytreeModal,
      showModal: showInformationFromMoneytreeModal,
    },
  ] = useModal();

  const [
    isShowSecurityAccountCooperationErrorModal,
    {
      hideModal: hideSecurityAccountCooperationErrorModal,
      showModal: showSecurityAccountCooperationErrorModal,
    },
  ] = useModal();

  const [
    isShowAccountAlignmentModal,
    {
      hideModal: hideAccountAlignmentModal,
      showModal: showAccountAlignmentModal,
    },
  ] = useModal();

  const [
    isShowAlignmentAcountLoginInfoChangeModal,
    {
      hideModal: hideAlignmentAcountLoginInfoChangeModal,
      showModal: showAlignmentAcountLoginInfoChangeModal,
    },
  ] = useModal();

  const [
    isShowUpdateAlignmentAccountModal,
    {
      hideModal: hideUpdateAlignmentAccountModal,
      showModal: showUpdateAlignmentAccountModal,
    },
  ] = useModal();

  const [
    isShowAccountUpdateStoppedModal,
    {
      hideModal: hideAccountUpdateStoppedModal,
      showModal: showAccountUpdateStoppedModal,
    },
  ] = useModal();

  const [
    isShowDuplicationAccountDeleteModal,
    {
      hideModal: hideDuplicationAccountDeleteModal,
      showModal: showDuplicationAccountDeleteModal,
    },
  ] = useModal();

  const [
    isShowWaitUntilNextUpdateModal,
    {
      hideModal: hideWaitUntilNextUpdateModal,
      showModal: showWaitUntilNextUpdateModal,
    },
  ] = useModal();

  const [
    isShowAlignmentPreparationModal,
    {
      hideModal: hideAlignmentPreparationModal,
      showModal: showAlignmentPreparationModal,
    },
  ] = useModal();
  const [
    isShowMonoSuspendedModal,
    { hideModal: hideMonoSuspendedModal, showModal: showMonoSuspendedModal },
  ] = useModal();

  const location = useLocation();
  const routing = useRouting();

  const {
    isReviseButtonActive,
    setIsReviseButtonActive,
  } = useCustomizeHeaderContext();

  const [
    showSyncAccountAssetResultModal,
    setShowSyncAccountAssetResultModal,
  ] = useState<boolean>(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showGoMoneytreeSiteDialog, setShowGoMoneytreeSiteDialog] = useState<
    boolean
  >();
  const [syncAccountAssetResult, setSyncAccountAssetResult] = useState<number>(
    6
  );
  const [beInRefresh, setBeInRefresh] = useState<boolean>(false);

  type MonoCooperationStatus = 'error' | 'error_009' | 'success' | 'roading';
  const [showStatus, setShowStatus] = useState<MonoCooperationStatus>();

  // 口座の状況
  useEffect(() => {
    if (brokerItem.Status === '000') {
      setShowStatus('success');
    } else if (brokerItem.Status === null || brokerItem.Status === '800') {
      setShowStatus('roading');
    } else if (brokerItem.Status === '009') {
      setShowStatus('error_009');
    } else {
      setShowStatus('error');
    }
  }, [brokerItem.Status]);

  const redirectPath = `${env.appURL}${location.pathname}`;

  const pushRouting = (({ pushRouting }) => (path: string) => (): void => {
    pushRouting({ path }, true);
  })(useRouting());
  const toAnotherServiceAlignment = pushRouting('/anotherservicealignment');

  const compBrokerId = [consts.roboType.minkabu] as string[];
  const isLongPeriodNotLogin = brokerItem.Status === '900';
  const isAccountdetail = location.pathname.startsWith('/accountdetail');

  useEffect(() => {
    const userStorage = getUserStorage();
    const getReviseButtonActive = (): boolean => {
      switch (brokerItem.AggregateSource) {
        case consts.AggregateSourceTypes.MONO:
          if (brokerItem.BrokerID === consts.MONOBrokerIds.SBI証券) {
            return true;
          } else if (
            brokerItem.BrokerID === consts.MONOBrokerIds.楽天証券 ||
            brokerItem.BrokerID === consts.MONOBrokerIds['JIS&T'] ||
            brokerItem.BrokerID === consts.MONOBrokerIds.NRK ||
            brokerItem.BrokerID ===
              consts.MONOBrokerIds.SBIベネフィットシステムズ
          ) {
            if (userStorage !== null && userStorage.Charged === true) {
              return true;
            } else {
              return false;
            }
          } else {
            return false;
          }
        case consts.AggregateSourceTypes.マネーツリー:
        case consts.AggregateSourceTypes.テコテック:
        case consts.AggregateSourceTypes.手入力:
          return true;
        default:
          return false;
      }
    };
    const isReviseButtonActive = getReviseButtonActive();
    setIsReviseButtonActive(isReviseButtonActive);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showAvailable = (item: GetUserBrokerResultItem): React.ReactElement => {
    const color = item.Available ? 'green-500' : 'bluegray-400';
    const text = ((): string => {
      switch (item.AggregateSource) {
        case consts.AggregateSourceTypes.テコテック:
        case consts.AggregateSourceTypes.MONO:
          return item.Available ? '集計対象' : '取引履歴のみ集計';
        case consts.AggregateSourceTypes.マネーツリー:
        case consts.AggregateSourceTypes.手入力:
          return item.Available ? '集計対象' : '集計対象外';
        default:
          return '';
      }
    })();

    return <div className={`text-${color}`}>{text}</div>;
  };

  const groupClass = (group: Group): string =>
    !isCategorized(group)
      ? 'unclassified'
      : `group${group.tabNum.toString().padStart(2, '0')}`;

  /*
    HACK: コンポーネントを呼ぶ画面によって変える必要があるが、このままだと他にも追加になったときに訳が分からなくなるので、
    propsでレイアウトパターンを渡すようにして判断する方がよさげ(cssで構造的に組んだものを渡せるようにでもいいかも)
  */
  const padding = editable || !onBrokerClick ? 'p-4' : 'py-4 pr-2 pl-6';

  type PositionListComponent = React.FC<{
    positions: Array<GetPositionsResultItem>;
    brokerItem: GetUserBrokerResultItem;
  }>;

  const PositionList: PositionListComponent = ((): PositionListComponent => {
    switch (positionListType) {
      case POSITION_LIST_TYPE['口座内訳']:
        return ({ positions, brokerItem }): React.ReactElement => (
          <>
            {positions.length !== 0 ? (
              <>
                {brokerItem.AggregateSource !==
                consts.AggregateSourceTypes.手入力 ? (
                  <>
                    <PositionListAccountBreakdown
                      positions={positions}
                      brokerItem={brokerItem}
                      sortable
                      expandable={false}
                      setUserPositions={setUserPositions}
                    />
                  </>
                ) : (
                  <></>
                )}
              </>
            ) : (
              <></>
            )}
          </>
        );
      case POSITION_LIST_TYPE['入出金']:
        return PositionListTransaction;
    }
  })();

  // 口座連携エラー
  const alignmentPreparationMessage = () => {
    return (
      <div className="pr-3 pb-2">
        <div
          className="flex w-full px-5 py-2 rounded-lg text-sm"
          style={{
            backgroundColor: `rgba(${0xff}, ${0xee}, ${0xf0}, 1)`,
          }}>
          <Icon name="alert" className="flex items-center h-5 text-red pr-2" />
          <div className="">更新に失敗しました</div>
          <div
            className="ml-auto text-link cursor-pointer"
            onClick={(e): void => {
              e.stopPropagation();
              showAlignmentPreparationModal();
            }}>
            詳細を確認
          </div>
        </div>
      </div>
    );
  };

  const onClickErrorInfo: MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation();

    if (isAccountdetail && isLongPeriodNotLogin) {
      showAccountUpdateStoppedModal();
    } else if (brokerItem.Status === '003') {
      showAlignmentAcountLoginInfoChangeModal();
    } else if (brokerItem.Status === '006') {
      showUpdateAlignmentAccountModal();
    } else if (brokerItem.Status === '902') {
      showDuplicationAccountDeleteModal();
    } else if (brokerItem.Status === '801') {
      showWaitUntilNextUpdateModal();
    } else {
      showSecurityAccountCooperationErrorModal();
    }
  };

  const onClickAlignmentInfo: MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation();
    showAccountAlignmentModal();
  };

  const errorMessage = () => {
    if (
      brokerItem.AggregateSource === consts.AggregateSourceTypes.MONO &&
      !(
        brokerItem.BrokerID === consts.MONOBrokerIds.SBI証券 ||
        brokerItem.BrokerID === consts.MONOBrokerIds.楽天証券 ||
        brokerItem.BrokerID === consts.MONOBrokerIds['JIS&T'] ||
        brokerItem.BrokerID ===
          consts.MONOBrokerIds.SBIベネフィットシステムズ ||
        brokerItem.BrokerID === consts.MONOBrokerIds.NRK
      )
    ) {
      return alignmentPreparationMessage();
    }

    if (
      showStatus === 'success' ||
      (!isAccountdetail && isExistError900 !== undefined && isExistError900)
    )
      return <></>;

    if (showStatus === 'roading')
      return (
        <>
          <div className="pr-3 pb-2">
            <div className="flex w-full px-5 py-2 rounded-lg text-sm bg-teal-75">
              <Icon
                name="reflesh"
                className="flex items-center h-5 text-teal-600 pr-2"
              />
              <div className="">資産情報取得中です</div>
              <div
                className="ml-auto text-link cursor-pointer"
                onClick={onClickAlignmentInfo}>
                詳細を確認
              </div>
            </div>
          </div>
        </>
      );

    return (
      <>
        <div className="pr-3 pb-2">
          <div
            className="flex w-full px-5 py-2 rounded-lg text-sm"
            style={{
              backgroundColor: `rgba(${0xff}, ${0xee}, ${0xf0}, 1)`,
            }}>
            <Icon
              name="alert"
              className="flex items-center h-5 text-red pr-2"
            />
            {showStatus === 'error' ? (
              <>
                <div className="">更新に失敗しました</div>
                <div
                  className="ml-auto text-link cursor-pointer"
                  onClick={onClickErrorInfo}>
                  詳細を確認
                </div>
              </>
            ) : (
              <></>
            )}
            {showStatus === 'error_009' ? (
              <>
                <div className="">資産情報取得中です</div>
                <div
                  className="ml-auto text-link cursor-pointer"
                  onClick={onClickErrorInfo}>
                  詳細を確認
                </div>
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
      </>
    );
  };

  const onClickMoneytreeInfo: MouseEventHandler<HTMLDivElement> = e => {
    e.stopPropagation();
    showInformationFromMoneytreeModal();
  };

  const onHideSecurityAccountCooperationErrorModal = (): void => {
    hideSecurityAccountCooperationErrorModal();
  };

  const doRegister = async (brokerID: string): Promise<void> => {
    // MEMO: MONO障害対応中は再連携を停止
    if (consts.IS_MONO_SUSPENDED) {
      showMonoSuspendedModal();
      return;
    }
    const convertBrokerID = convertToMonoBrokerId(brokerID);

    const res = await api.prepareUserBroker({ brokerID: convertBrokerID });
    if (res.Success) {
      window.location.href = `${env.monoAccountURL}/register-external-integration-sec-account/${convertBrokerID}?onetimeToken=${res.OnetimeToken}&provisionalId=${res.ProvisionalID}&callbackUrl=${env.appURL}/userbrokerlink&returnUri=${env.appURL}/securityaccounts&completeUrl=${env.appURL}/securityaccounts`;
    } else if (!res.Success && res.Status === '410') {
    }
  };

  const doGoMoneytreeSiteOK = (): void => {
    window.open(
      `${env.moneytreeManagementURL}/connection/${brokerItem?.Account}?client_id=${env.moneytreeClientId}`,
      '_blank'
    );
    setShowGoMoneytreeSiteDialog(false);
  };

  const doDeleteOKCritter = (brokerItem: GetUserBrokerResultItem): void => {
    setLoading(true);

    deleteAccount(brokerItem.BrokerID, brokerItem.Account)
      .then(_ => {
        routing.backRouting(true);
      })
      .catch(_ => {
        setLoading(false);
      });
  };
  const doDeleteOKMono = (brokerItem: GetUserBrokerResultItem): void => {
    setLoading(true);

    deleteAccount(brokerItem.BrokerID, brokerItem.Account)
      .then(_ => {
        routing.backRouting(true);
      })
      .catch(_ => {
        setLoading(false);
      });
  };
  const doDeleteOKManual = (brokerItem: GetUserBrokerResultItem): void => {
    deleteManualAsset(brokerItem.Account).then(res => {
      routing.backRouting(true);
    });
  };

  const doDeleteOK = (): void => {
    if (!brokerItem) return;

    switch (brokerItem.AggregateSource) {
      case consts.AggregateSourceTypes.テコテック:
        doDeleteOKCritter(brokerItem);
        break;
      case consts.AggregateSourceTypes.MONO:
        doDeleteOKMono(brokerItem);
        break;
      case consts.AggregateSourceTypes.手入力:
        doDeleteOKManual(brokerItem);
        break;
    }

    setShowDeleteConfirm(false);
  };

  const doAfterSyncAccountAsset = (): void => {
    doSyncAccountAsset(brokerItem);
  };

  const doSyncAccountAsset = async (
    userBroker: GetUserBrokerResultItem
  ): Promise<void> => {
    hideSecurityAccountCooperationErrorModal();
    setLoading(true);
    setShowSyncAccountAssetResultModal(true);
    const ret = await syncAccountAsset(userBroker.Account);
    setSyncAccountAssetResult(ret.ResultCode);
    setLoading(false);
  };

  const doRefreshMoneytree = (): void => {
    if (!beInRefresh && onRefreshMoneytree) {
      setBeInRefresh(true);
      refreshMoneytree().then(ret => {
        onRefreshMoneytree();
        setBeInRefresh(false);
      });
    }
  };

  // 資産・負債
  const [dispType, setDispType] = useState<DispType>('資産');
  const [chartData, setChartData] = useState<ChartDatum[] | null>(null);
  const [amount, setAmount] = useState<number | null>(null);

  const isTotalAssetAmountEmpty =
    accountHistory === undefined ||
    accountHistory.totalAssetHistories.length === 0 ||
    accountHistory.totalAssetHistories.every(
      list => list.totalAssetAmount === 0
    );
  const isDebtAmountEmpty =
    accountHistory === undefined ||
    accountHistory.debtAssetHistories.length === 0 ||
    accountHistory.debtAssetHistories.every(list => list.debtAmount === 0);

  useEffect(() => {
    if (accountHistory === undefined) return setChartData(null);

    type ChartType = 'total' | 'debt' | 'none';
    let chartType: ChartType = 'none';
    if (!isTotalAssetAmountEmpty && !isDebtAmountEmpty) {
      chartType = dispType === '資産' ? 'total' : 'debt';

      const num =
        dispType === '資産'
          ? accountHistory.totalAssetHistories[0].totalAssetAmount
          : accountHistory.debtAssetHistories[0].debtAmount;
      setAmount(num);
    } else if (isTotalAssetAmountEmpty && !isDebtAmountEmpty) {
      chartType = 'debt';

      setDispType('負債');
      setAmount(brokerItem.TotalAssetAmount);
    } else if (!isTotalAssetAmountEmpty && isDebtAmountEmpty) {
      chartType = 'total';

      setDispType('資産');
      setAmount(brokerItem.TotalAssetAmount);
    } else {
      chartType = 'none';

      setAmount(brokerItem.TotalAssetAmount);
    }

    const data =
      chartType === 'total'
        ? accountHistory.totalAssetHistories.map(item => {
            return {
              x: getDate(item.day),
              y: item.totalAssetAmount,
            };
          })
        : chartType === 'debt'
        ? accountHistory.debtAssetHistories.map(item => {
            return {
              x: getDate(item.day),
              y: item.debtAmount,
            };
          })
        : null;
    const sortData =
      data !== null ? data.sort((a, b) => (a.x > b.x ? 1 : -1)) : null;
    setChartData(sortData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispType]);

  return (
    <>
      <div
        className={`w-full ${padding} ${
          !isAccountdetail && index !== 0 ? 'border-t border-gray-300' : ''
        }
          ${isAccountdetail ? '' : 'cursor-pointer'}`}
        onClick={(): void => onBrokerClick && onBrokerClick(brokerItem)}
        key={brokerItem.Account}>
        {errorMessage()}
        <div className="mb-2 flex text-xs">
          <div className="flex-grow text-bluegray-500 flex">
            {getAggregateSourceName(brokerItem.AggregateSource)}
            {brokerItem.AggregateSource ===
              consts.AggregateSourceTypes.マネーツリー &&
              isAccountdetail && (
                <div className="cursor-pointer" onClick={onClickMoneytreeInfo}>
                  <Icon
                    name="info"
                    className="text-base text-bluegray-400 ml-2"
                  />
                </div>
              )}
          </div>
          <div
            onClick={(e): void => {
              onChangeState && onChangeState(brokerItem);
              e.stopPropagation();
              onBrokerClick && onBrokerClick(brokerItem);
            }}
            className="flex tap-color-none cursor-pointer">
            {showAvailable(brokerItem)}
            {editable ? (
              <div className="toggle ml-2">
                <div className="checkbox">
                  <input
                    id={brokerItem.Account}
                    type="checkbox"
                    checked={brokerItem.Available}
                    onChange={(): void => {
                      return;
                    }}
                  />
                  <label htmlFor={brokerItem.Account}></label>
                </div>
              </div>
            ) : null}
            {/* コンテンツの幅を揃えるためにダミーの要素を入れておく */}
            {onBrokerClick ? <div className="ml-1 w-6"></div> : null}
          </div>
        </div>
        {isAccountdetail && !isDebtAmountEmpty && !isTotalAssetAmountEmpty && (
          <div className="flex flex-row justify-center text-xs mb-4">
            <div
              className={`py-0.5 px-3 ${
                dispType === '資産' ? 'text-white bg-blue-700' : ''
              } border border-blue-700 rounded-l cursor-pointer`}
              onClick={(): void => setDispType('資産')}>
              資産
            </div>
            <div
              className={`py-0.5 px-3 ${
                dispType === '負債' ? 'text-white bg-blue-700' : ''
              } border border-blue-700 rounded-r cursor-pointer`}
              onClick={(): void => setDispType('負債')}>
              負債
            </div>
          </div>
        )}
        <div className="flex">
          <div className="flex-grow">
            <div className="flex items-center">
              <div className="text-sm font-bold">
                {isAccountdetail
                  ? dispType === '資産'
                    ? '資産総額'
                    : '負債金額'
                  : '資産総額'}
              </div>
              <div className="flex flex-row ml-auto text-lg font-bold text-right">
                {!isAccountdetail && isLongPeriodNotLogin && (
                  <div className="icon-alert items-start mr-2 mt-1 text-base text-red-400"></div>
                )}
                {formatNumber(
                  isAccountdetail ? amount : brokerItem.TotalAssetAmount,
                  {
                    unitName: '円',
                    noValueText: '---円',
                  }
                )}
              </div>
            </div>
            <div className="flex justify-between w-full items-center">
              {group && (
                <div
                  className={`asset-list-item-group-badge  ${
                    group ? groupClass(group) : ''
                  }`}>
                  <i className="icon-folder-outline"></i>
                  {group.name}
                </div>
              )}
              <div
                className={`flex justify-end text-xs mt-1 text-gray-500 ${
                  isReviseButtonActive &&
                  brokerItem.AggregateSource !==
                    consts.AggregateSourceTypes.手入力 &&
                  brokerItem.AggregateSource !==
                    consts.AggregateSourceTypes.テコテック
                    ? 'cursor-pointer'
                    : 'cursor-default'
                }`}>
                <div className="ml-4">
                  {formatDateForString(brokerItem.UpdTime, 'yyyy/MM/dd HH:mm')}
                  <span
                    className={`ml-1 ${
                      !isAccountdetail
                        ? ''
                        : isReviseButtonActive
                        ? ''
                        : 'text-gray-300'
                    }`}>
                    更新
                  </span>
                </div>
                {brokerItem.Status !== '902' ? (
                  <>
                    {onSyncAccountAsset &&
                      brokerItem.AggregateSource ===
                        consts.AggregateSourceTypes.MONO && (
                        <>
                          <div
                            className="ml-1 text-sm"
                            onClick={(): void => {
                              isReviseButtonActive &&
                                onSyncAccountAsset(brokerItem);
                            }}>
                            <div
                              className={`icon-reflesh ${
                                isReviseButtonActive ? '' : 'text-gray-300'
                              }`}></div>
                          </div>
                        </>
                      )}
                    {onSyncAccountAsset &&
                      brokerItem.AggregateSource ===
                        consts.AggregateSourceTypes.マネーツリー && (
                        <>
                          <div
                            className="ml-1 text-sm"
                            onClick={doRefreshMoneytree}>
                            <div
                              className={`icon-reflesh ${
                                beInRefresh
                                  ? 'animation animation-infinite animation-rotate'
                                  : ''
                              }`}></div>
                          </div>
                        </>
                      )}
                  </>
                ) : (
                  <></>
                )}
              </div>
            </div>
            {getIsShowDisplayableAssetTypeModalButton({
              brokerId: brokerItem.BrokerID,
            }) && (
              <div className="flex w-full flex-row items-center">
                <div className="ml-auto pt-4">
                  <DisplayableAssetTypeModalButton
                    isMoneytree={
                      brokerItem.AggregateSource ===
                      consts.AggregateSourceTypes.マネーツリー
                    }
                  />
                </div>
              </div>
            )}
          </div>
          {onBrokerClick ? (
            <div className="flex items-center ml-1">
              <Icon name="chevron-right" className="text-2xl text-gray-500" />
            </div>
          ) : null}
        </div>
        {brokerItem.AggregateSource ===
          consts.AggregateSourceTypes.テコテック && (
          <div className="pt-4 pr-3 pb-2">
            <NeedsMigrateCard
              migrate={(e): void => {
                doRegister(brokerItem.BrokerID);
                e.stopPropagation();
              }}
              onClickAgreement={toAnotherServiceAlignment}
            />
          </div>
        )}
      </div>
      {brokerItem.AggregateSource !== 99 && brokerItem.BrokerType !== 99 && (
        <>
          {isAccountdetail && chartData !== null && (
            <div className="mb-8 mx-2">
              {dispType === '資産' ? (
                <AssetsChart {...{ data: chartData, dispType }} />
              ) : (
                <DebtChart {...{ data: chartData, dispType }} />
              )}
            </div>
          )}
        </>
      )}
      {positions ? (
        <PositionList positions={positions} brokerItem={brokerItem} />
      ) : null}
      {positions && compBrokerId.includes(brokerItem.BrokerID) ? (
        <div className=" my-10 mx-5">
          <div className="text-center py-2 px-4 border rounded border-link text-link bg-white">
            <span className="text-sm">MINKABU ROBO マイページへ</span>
            <i className="icon-external-link ml-2 text-lg"></i>
          </div>
        </div>
      ) : null}
      <InformationFromMoneytreeModal
        hideModal={hideInformationFromMoneytreeModal}
        isShowModal={
          isShowInformationFromMoneytreeModal &&
          brokerItem.AggregateSource ===
            consts.AggregateSourceTypes.マネーツリー
        }
      />
      <AccountDeleteConfirmModal
        delete={doDeleteOK}
        close={(): void => setShowDeleteConfirm(false)}
        targetGoal={(group && isCategorized(group) && group.targetGoal) || null}
        groupName={(group && group.name) || ''}
        isShow={showDeleteConfirm}
      />
      {showGoMoneytreeSiteDialog && (
        <GoMoneytreeSiteDialog
          onOK={doGoMoneytreeSiteOK}
          onCancel={(): void => setShowGoMoneytreeSiteDialog(false)}
        />
      )}
      {isLoading && (
        <div className="absolute top-0 left-0 h-full w-full bg-gray-800 opacity-75 flex justify-center items-center z-20">
          <Loading
            loading={true}
            circularLoading={true}
            style={{
              backgroundColor: 'transparent',
            }}
          />
        </div>
      )}
      <SecurityAccountCooperationErrorModal
        hideModal={onHideSecurityAccountCooperationErrorModal}
        isShowModal={isShowSecurityAccountCooperationErrorModal}
        brokerItem={brokerItem}
        doAfterSyncAccountAsset={doAfterSyncAccountAsset}
      />
      <AccountAlignmentModal
        hideModal={hideAccountAlignmentModal}
        isShowModal={isShowAccountAlignmentModal}
      />
      <SyncAccountAssetResultModal
        isShowModal={showSyncAccountAssetResultModal}
        hideModal={(): void => setShowSyncAccountAssetResultModal(false)}
        resultCode={syncAccountAssetResult}
        isLoading={isLoading}
      />
      <AlignmentAcountLoginInfoChangeModal
        brokerItem={brokerItem}
        redirectPath={redirectPath}
        hide={hideAlignmentAcountLoginInfoChangeModal}
        isShow={isShowAlignmentAcountLoginInfoChangeModal}
      />
      <UpdateAlignmentAccountModal
        brokerID={brokerItem.BrokerID}
        account={brokerItem.Account}
        redirectPath={redirectPath}
        hide={hideUpdateAlignmentAccountModal}
        isShow={isShowUpdateAlignmentAccountModal}
      />
      <AccountUpdateStoppedModal
        redirectPath={redirectPath}
        brokerItems={brokerItems}
        hide={hideAccountUpdateStoppedModal}
        isShow={isShowAccountUpdateStoppedModal}
      />
      <DuplicationAccountDeleteModal
        hide={hideDuplicationAccountDeleteModal}
        isShow={isShowDuplicationAccountDeleteModal}
        isLoading={isLoading}
        doDeleteOKMono={() => {
          // MEMO: MONO障害対応中は重複口座の削除を停止
          if (consts.IS_MONO_SUSPENDED) {
            hideDuplicationAccountDeleteModal();
            showMonoSuspendedModal();
            return;
          }
          doDeleteOKMono(brokerItem);
        }}
      />
      <WaitUntilNextUpdateModal
        hideModal={hideWaitUntilNextUpdateModal}
        isShowModal={isShowWaitUntilNextUpdateModal}
        errorCode={'801'}
      />
      <AlignmentPreparationModal
        hide={hideAlignmentPreparationModal}
        isShow={isShowAlignmentPreparationModal}
      />
      <MonoSuspendedModal
        hideModal={hideMonoSuspendedModal}
        isShowModal={isShowMonoSuspendedModal}
      />
    </>
  );
};

const UserBrokerItem = (
  props: UserBrokerItemProps
): React.ReactElement | null => {
  const { BrokerType } = props.brokerItem;
  const positionListType: PositionListType =
    BrokerType === consts.BrokerType['銀行'] ||
    BrokerType === consts.BrokerType['クレジットカード'] ||
    BrokerType === consts.BrokerType['電子マネー']
      ? POSITION_LIST_TYPE['入出金']
      : POSITION_LIST_TYPE['口座内訳'];
  return <UserBrokerItemInternal {...{ ...props, positionListType }} />;
};

export default UserBrokerItem;
